'use client'
import { MantineProvider, createTheme } from '@mantine/core'
import '@mantine/core/styles.css'
import { CampaignNotFoundGuard } from '@/components/Layout/CampaignNotFoundGuard'
import { CampaignPeriodGuard } from '@/components/Layout/CampaignPeriodGuard'
import { InitialQuestionnaireInAppMachineContext } from '@/components/Layout/questionnaire/InitialQuestionnaire'
import { authV2Url } from '@/core/app/authUrls'
import { loadCampaignColors, saveCampaignColors } from '@/core/app/campaignColorCache'
import { isValidCampaignSlug, useCampaignSlug } from '@/core/app/useCampaignSlug'
import { AppMachineContext, RaundLifeCycleProvider } from '@/core/app/useRaundLifeCycle'
import { useReloadOnMemberChange } from '@/core/app/useReloadOnMemberChange'
import { useChangeLanguage } from '@/core/i18n/useChangeLanguage'
import { useLocale } from '@/core/i18n/useLocale'
import { useTrackNavigationHistory } from '@/core/navigation/useTrackNavigationHistory'
import { useRegularPageGuard } from '@/domain/campaign/useRegularPageGuard'
import { queryPersister as persister } from '@/lib/query/queryPersister'
import { useTokenStore } from '@/store/useTokenStore'
import { raundTheme } from '@/theme'
// import { GoogleAnalytics } from '@/components/Layout/GoogleAnalytics'
import { getContrastTextColor, resolveOnColor } from '@/theme/contrastText'
import { generateColorScale } from '@/theme/generateColorScale'
import type { AppProps } from 'next/app'
import Head from 'next/head'
import { useRouter } from 'next/router'
import React, { useEffect, useMemo, useRef } from 'react'
import 'swiper/css'
import 'swiper/css/virtual'
import 'swiper/css/pagination'
import 'maplibre-gl/dist/maplibre-gl.css'
import '@/styles/globals.css'
import '@/styles/Common.css'
import '@/styles/component/Dialog.css'
import '@/i18n/config'
import { initMaplibreWorker } from '@/lib/map/initMaplibreWorker'
import { initPmtilesProtocol } from '@/lib/map/initPmtilesProtocol'
import { QueryClient } from '@tanstack/react-query'
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client'

// MapLibre の Worker URL と PMTiles プロトコルをアプリ起動時に一度だけ設定する
initMaplibreWorker()
initPmtilesProtocol()

const PERSIST_QUERY_KEYS = ['campaign', 'spots', 'menus', 'sheets', 'rewards']

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: 1000 * 60 * 60 * 24, // 24時間（persister の maxAge と合わせる）
    },
  },
})

/**
 * Next.js エントリーポイント。
 * PersistQueryClientProvider を最上位に配置し、子コンポーネント（AppContent）で
 * useQuery を使えるようにするため、Provider とコンテンツを2層に分離している。
 */
function RaundApplication({ Component, pageProps }: AppProps) {
  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister,
        dehydrateOptions: {
          shouldDehydrateQuery: (query) => PERSIST_QUERY_KEYS.includes(String(query.queryKey[0])),
        },
      }}
    >
      <RaundApplicationLifeCycle Component={Component} pageProps={pageProps} />
    </PersistQueryClientProvider>
  )
}

function RaundApplicationLifeCycle({ Component, pageProps }: Pick<AppProps, 'Component' | 'pageProps'>) {
  return (
    <RaundLifeCycleProvider>
      <AppContent Component={Component} pageProps={pageProps} />
    </RaundLifeCycleProvider>
  )
}

/**
 * アプリケーション本体。
 * PersistQueryClientProvider の内側に配置することで useQuery が利用可能になり、
 * キャンペーン API から取得した mainColor / subColor を Mantine テーマと CSS 変数の両方に動的反映する。
 */
function AppContent({ Component, pageProps }: Pick<AppProps, 'Component' | 'pageProps'>) {
  const router = useRouter()
  const actorRef = AppMachineContext.useActorRef()
  const { campaignSlug } = useCampaignSlug()
  const { locale } = useLocale()

  const backgroundImage = AppMachineContext.useSelector((s) => s.context.campaignData?.backgroundImage ?? null)
  const primaryColor = AppMachineContext.useSelector((s) => s.context.primaryColor)
  const subColor = AppMachineContext.useSelector((s) => s.context.subColor)
  const campaignData = AppMachineContext.useSelector((s) => s.context.campaignData)

  // キャンペーン設定に基づく言語初期化
  useChangeLanguage({ campaign: campaignData })

  // マイナビキャンペーンで通常ページにアクセスした場合、TOPへリダイレクト
  useRegularPageGuard()

  // 戻るボタンの「アプリ内に戻り先があるか」判定のため、アプリ内遷移の履歴キー列を記録する（#3665）
  useTrackNavigationHistory()

  // ページ遷移完了時にマスタ更新チェックをトリガーする。
  // 同一ページ内のクエリ更新（スポット一覧のタブ切替 ?tab=list や fromQr クエリの除去）は対象外にする（#3665）。
  // shallow だけでは判定しない。Next.js は popstate 時に「復元先の shallow && 直前の遷移の shallow」を渡すため、
  // ページが変わる戻る操作でも shallow=true になり得る。パスが変わっていないことも合わせて確認する
  useEffect(() => {
    const pathOf = (url: string) => url.split(/[?#]/)[0].replace(/\/$/, '')
    let lastPath = pathOf(window.location.pathname)
    const handleRouteChange = (url: string, { shallow }: { shallow: boolean }) => {
      const path = pathOf(url)
      const isSamePage = shallow && path === lastPath
      lastPath = path
      if (isSamePage) return
      actorRef.send({ type: 'PAGE_NAVIGATED' })
    }
    router.events.on('routeChangeComplete', handleRouteChange)
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange)
    }
  }, [router.events, actorRef])

  // バックグラウンドタブからフォアグラウンドに戻ったとき、期限切れトークンを即座に再取得する。
  // ブラウザはバックグラウンドタブの setTimeout を抑制するため、XState の after タイマーが
  // 発火しないまま idToken が期限切れになる可能性がある。visibilitychange で補完する。
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        actorRef.send({ type: 'PAGE_VISIBLE' })
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [actorRef])

  // 「戻る」で bfcache から復元したとき・裏のタブが表に戻ったとき、別タブで会員が入れ替わっていたら読み直す（#3760）
  useReloadOnMemberChange(campaignSlug)

  // 認証付き API が 401 を返したとき（request.ts で notify401() が呼ばれる）、
  // XState の tokenRefresh ステートマシンに UNAUTHORIZED_401 イベントを送り即座にリフレッシュをトリガーする。
  const pendingTokenRefresh = useTokenStore((s) => s.pendingTokenRefresh)
  const clearPendingTokenRefresh = useTokenStore((s) => s.clearPendingTokenRefresh)
  useEffect(() => {
    if (!pendingTokenRefresh) return
    clearPendingTokenRefresh()
    actorRef.send({ type: 'UNAUTHORIZED_401' })
  }, [pendingTokenRefresh, clearPendingTokenRefresh, actorRef])

  // 認証済み API が 403 LINE_NOT_FRIEND を返したとき（request.ts で notifyLineFriendRequired() が呼ばれる）、
  // LINE友達追加フローへリダイレクトする。
  const pendingLineFriendRequired = useTokenStore((s) => s.pendingLineFriendRequired)
  const clearPendingLineFriendRequired = useTokenStore((s) => s.clearPendingLineFriendRequired)
  useEffect(() => {
    if (!pendingLineFriendRequired) return
    clearPendingLineFriendRequired()
    window.location.href = authV2Url.forceFriendAdd(campaignSlug, locale)
  }, [pendingLineFriendRequired, clearPendingLineFriendRequired, campaignSlug, locale])

  // 認証済み API が 403 REAUTH_REQUIRED を返したとき（request.ts で notifyReauthRequired() が呼ばれる）、
  // auth の再認証画面へ移動する（#3610 / #3635）。
  // return_to には「その画面を開き直せる URL」を入れる。クエリ（商品一覧の redirect_uri 等）・ハッシュ込みの
  // 現在 URL をそのまま渡す。画面の状態（選んだ商品・スクロール位置）は auth 側では戻せない（仕様で決定済み）。
  // API のエラー起点で走るため hydration 後であることが確実で、window.location.href に '[campaign]' は残らない。
  // 再認証後は旧トークンが失効するため、別タブではなく同じタブで移動する。
  const pendingReauthRequired = useTokenStore((s) => s.pendingReauthRequired)
  const clearPendingReauthRequired = useTokenStore((s) => s.clearPendingReauthRequired)
  useEffect(() => {
    if (!pendingReauthRequired) return
    clearPendingReauthRequired()
    // 識別子が未確定（'[campaign]' 等）のときは存在しないキャンペーンの URL になるため移動しない
    if (!isValidCampaignSlug(campaignSlug)) return
    window.location.href = authV2Url.reauth(campaignSlug, window.location.href, locale)
  }, [pendingReauthRequired, clearPendingReauthRequired, campaignSlug, locale])

  // 401 起因のリフレッシュが完了したらページをリロードする。
  // tokenRefresh ステートが refreshing → waiting に遷移したとき（= リフレッシュ成功）かつ
  // shouldReloadAfterRefresh が true（= 401 トリガー）の場合のみリロードする。
  // 通常のタイマー起動リフレッシュは shouldReloadAfterRefresh が false のため対象外。
  // ARカメラ使用中（pageReloadSuppressed: true）はリロードを抑止する。
  const isTokenRefreshWaiting = AppMachineContext.useSelector((s) => s.matches({ phase3: { tokenRefresh: 'waiting' } }))
  const shouldReloadAfterRefresh = useTokenStore((s) => s.shouldReloadAfterRefresh)
  const clearShouldReloadAfterRefresh = useTokenStore((s) => s.clearShouldReloadAfterRefresh)
  const pageReloadSuppressed = useTokenStore((s) => s.pageReloadSuppressed)
  const prevTokenRefreshWaitingRef = useRef(isTokenRefreshWaiting)
  useEffect(() => {
    const justEnteredWaiting = isTokenRefreshWaiting && !prevTokenRefreshWaitingRef.current
    prevTokenRefreshWaitingRef.current = isTokenRefreshWaiting
    if (justEnteredWaiting && shouldReloadAfterRefresh && !pageReloadSuppressed) {
      clearShouldReloadAfterRefresh()
      window.location.reload()
    }
  }, [isTokenRefreshWaiting, shouldReloadAfterRefresh, clearShouldReloadAfterRefresh, pageReloadSuppressed])

  const primaryScale = useMemo(() => generateColorScale(primaryColor), [primaryColor])
  const secondaryScale = useMemo(() => generateColorScale(subColor), [subColor])

  const dynamicTheme = useMemo(
    () =>
      createTheme({
        ...raundTheme,
        colors: {
          ...raundTheme.colors,
          primary: primaryScale,
          secondary: secondaryScale,
        },
      }),
    [primaryScale, secondaryScale],
  )

  useEffect(() => {
    // キャンペーンデータ未取得時（campaignData === null）はフォールバック色で CSS 変数を上書きしない。
    // _document.tsx の同期スクリプトが localStorage キャッシュから先行適用済みのため、
    // フォールバック色で上書きするとリロード時にフラッシュが再発し、誤った色が localStorage に保存される。
    if (campaignData === null) {
      // localStorage キャッシュを再適用してフォールバック色による上書きを防ぐ
      if (isValidCampaignSlug(campaignSlug)) {
        const cached = loadCampaignColors(campaignSlug)
        if (cached) {
          const cachedPrimaryScale = generateColorScale(cached.mainColor)
          document.documentElement.style.setProperty('--main-color', cached.mainColor)
          document.documentElement.style.setProperty('--sub-color', cached.subColor)
          // 背景に敷いたときの文字色（#3529）。保存済みの値があればそれを使い、
          // 旧バージョンのキャッシュ（未保存）の場合はここで算出する。
          document.documentElement.style.setProperty(
            '--on-main-color',
            cached.onMainColor ?? resolveOnColor(cached.mainColor, cached.subColor),
          )
          document.documentElement.style.setProperty(
            '--on-sub-color',
            cached.onSubColor ?? getContrastTextColor(cached.subColor),
          )
          document.documentElement.style.setProperty(
            '--main-color-50',
            'color-mix(in srgb, var(--main-color) 50%, transparent)',
          )
          document.documentElement.style.setProperty(
            '--main-color-20',
            'color-mix(in srgb, var(--main-color) 20%, transparent)',
          )
          document.documentElement.style.setProperty('--mantine-primary-color-filled', cached.mainColor)
          document.documentElement.style.setProperty('--mantine-primary-color-filled-hover', cachedPrimaryScale[6])
          document.documentElement.style.setProperty('--mantine-primary-color-light', cachedPrimaryScale[0])
          document.documentElement.style.setProperty('--mantine-primary-color-light-hover', cachedPrimaryScale[1])
          document.documentElement.style.setProperty('--mantine-color-primary-filled', cached.mainColor)
          for (let i = 0; i < 10; i++) {
            document.documentElement.style.setProperty(`--mantine-color-primary-${i}`, cachedPrimaryScale[i])
          }
        }
      }
      return
    }

    const mainColor = primaryScale[5]
    const mainColorHover = primaryScale[6]
    const mainColorLight = primaryScale[0]
    const mainColorLightHover = primaryScale[1]
    const subColor = secondaryScale[5]

    // メインカラー背景の上に置く文字色。キャンペーン指定の sub-color が AA を満たすなら
    // それを尊重し、満たさないときだけ白 or 黒へ退避する（#3529）
    const onMainColor = resolveOnColor(mainColor, subColor)
    // サブカラー背景の上に置く色は、既存実装が #000000 / #ffffff で割れており
    // 「キャンペーン指定色」に相当するものが無いため白黒判定のみとする
    const onSubColor = getContrastTextColor(subColor)

    document.documentElement.style.setProperty('--main-color', mainColor)
    document.documentElement.style.setProperty('--sub-color', subColor)
    document.documentElement.style.setProperty('--on-main-color', onMainColor)
    document.documentElement.style.setProperty('--on-sub-color', onSubColor)
    document.documentElement.style.setProperty(
      '--main-color-50',
      'color-mix(in srgb, var(--main-color) 50%, transparent)',
    )
    document.documentElement.style.setProperty(
      '--main-color-20',
      'color-mix(in srgb, var(--main-color) 20%, transparent)',
    )

    // Mantine のプライマリカラー CSS 変数を上書き。
    // 静的 CSS バンドルが --mantine-primary-color-* を blue に設定しているため、
    // インラインスタイル（最高優先度）でキャンペーンのメインカラーに強制上書きする。
    // --mantine-primary-color-* = static shorthand、--mantine-color-primary-* = runtime per-color-key 変数。
    // 両方上書きすることで全コンポーネントに対応する。
    document.documentElement.style.setProperty('--mantine-primary-color-filled', mainColor)
    document.documentElement.style.setProperty('--mantine-primary-color-filled-hover', mainColorHover)
    document.documentElement.style.setProperty('--mantine-primary-color-light', mainColorLight)
    document.documentElement.style.setProperty('--mantine-primary-color-light-hover', mainColorLightHover)
    // --mantine-color-primary-{0..9} をすべて上書き。
    // Button.tsx の pressed(-7) / ghost-pressed(-1) など全スロットをキャンペーンカラーに揃える。
    document.documentElement.style.setProperty('--mantine-color-primary-filled', mainColor)
    for (let i = 0; i < 10; i++) {
      document.documentElement.style.setProperty(`--mantine-color-primary-${i}`, primaryScale[i])
    }

    if (backgroundImage) {
      document.documentElement.style.setProperty('--background-image', `url(${JSON.stringify(backgroundImage)})`)
    } else {
      document.documentElement.style.removeProperty('--background-image')
    }

    // キャンペーンデータ確定後のみ localStorage に保存（フォールバック色を誤って永続化しない）
    if (isValidCampaignSlug(campaignSlug)) {
      // _document.tsx の FOUC 防止スクリプトが輝度計算を複製せずに済むよう、
      // 算出済みの文字色もあわせて保存する（#3529）
      saveCampaignColors(campaignSlug, mainColor, subColor, onMainColor, onSubColor)
    }
  }, [primaryScale, secondaryScale, backgroundImage, campaignSlug, campaignData])

  // const fqdnApi = (process.env.NEXT_PUBLIC_API_URL ?? '').replace('https://', '')
  // const fqdnApp = (process.env.NEXT_PUBLIC_SITEURL ?? '').replace('https://', '')

  return (
    <>
      <Head>
        {/* 本当は_document.tsxに置くのが正解だが、動的に生成したいため_app.tsxに置いている */}
        {!!campaignSlug && campaignSlug !== '[campaign]' && (
          <link rel="manifest" href={`/manifest.json?campaign=${campaignSlug}`} />
        )}
      </Head>
      <MantineProvider theme={dynamicTheme}>
        <InitialQuestionnaireInAppMachineContext />
        {/*<GoogleAnalytics gaId={process.env.NEXT_PUBLIC_GAID} linkerDomains={[fqdnApp, fqdnApi]} />*/}
        <CampaignNotFoundGuard>
          <CampaignPeriodGuard>
            <Component {...pageProps} />
          </CampaignPeriodGuard>
        </CampaignNotFoundGuard>
      </MantineProvider>
    </>
  )
}

export default RaundApplication
